import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Truck, Plus, Search, Edit2, Trash2, X, Phone, Mail, MapPin, User, Hash } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario/proveedores/`;

export default function ProveedoresManager() {
    const [proveedores, setProveedores] = useState([]);
    const [, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '', ruc: '', contacto: '', telefono: '', email: '', direccion: ''
    });
    const [formError, setFormError] = useState(null);


    const fetchProveedores = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API);
            setProveedores(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProveedores(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        try {
            if (editing) {
                await axios.put(`${API}${editing.id}/`, formData);
            } else {
                await axios.post(API, formData);
            }
            setIsModalOpen(false);
            setEditing(null);
            setFormData({ nombre: '', ruc: '', contacto: '', telefono: '', email: '', direccion: '' });
            fetchProveedores();
        } catch (err) {
            const d = err.response?.data;
            setFormError(d?.nombre?.[0] || d?.ruc?.[0] || 'Error al guardar proveedor.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este proveedor?')) return;
        try {
            await axios.delete(`${API}${id}/`);
            fetchProveedores();
        } catch (e) {
            alert('No se puede eliminar el proveedor. Solo se pueden desactivar si tienen compras vinculadas.');
        }
    };

    const openEdit = (p) => {
        setEditing(p);
        setFormData({ ...p });
        setFormError(null);
        setIsModalOpen(true);
    };

    const filtered = proveedores.filter(p =>
        p.nombre.toLowerCase().includes(search.toLowerCase()) ||
        (p.ruc && p.ruc.includes(search))
    );

    return (
        <div className="space-y-6">
            {/* Top Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10 gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl">
                        <Truck size={24} className="text-brand-800" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Proveedores</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">Gestiona tus suministros y contactos comerciales</p>
                    </div>
                </div>

                <div className="flex w-full md:w-auto gap-3">
                    <div className="relative flex-1 md:w-64">
                        <Search className="icono-campo" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar proveedor..."
                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500/50 transition-all"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => { setEditing(null); setFormData({ nombre: '', ruc: '', contacto: '', telefono: '', email: '', direccion: '' }); setFormError(null); setIsModalOpen(true); }}
                        className="bg-brand-500 hover:bg-brand-600 text-negro-950 px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-brand-500/20 active:scale-95"
                    >
                        <Plus size={20} />
                        <span className="hidden sm:inline">Nuevo Proveedor</span>
                    </button>
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                <AnimatePresence>
                    {filtered.map(p => (
                        <motion.div
                            key={p.id}
                            layout
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 p-6 rounded-2xl hover:border-brand-500/50 transition-all group relative"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="bg-slate-100 dark:bg-white/5 p-3 rounded-xl group-hover:bg-brand-500/10 transition-colors">
                                    <Truck size={24} className="text-brand-800" />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => openEdit(p)} className="p-2 bg-slate-100 dark:bg-white/5 hover:bg-brand-500/20 text-brand-800 rounded-lg transition-colors"><Edit2 size={16} /></button>
                                    <button onClick={() => handleDelete(p.id)} className="p-2 bg-slate-100 dark:bg-white/5 hover:bg-error-500/20 text-error-400 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">{p.nombre}</h3>
                            <div className="flex items-center gap-2 text-xs font-mono text-slate-500 dark:text-gray-500 mb-4 bg-slate-100 dark:bg-black/30 w-fit px-2 py-1 rounded">
                                <Hash size={12} /> {p.ruc || 'S/ RUC'}
                            </div>

                            <div className="space-y-3">
                                {p.contacto && (
                                    <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-gray-400">
                                        <User size={16} className="text-brand-800/50" />
                                        <span>{p.contacto}</span>
                                    </div>
                                )}
                                {p.telefono && (
                                    <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-gray-400">
                                        <Phone size={16} className="text-brand-800/50" />
                                        <span>{p.telefono}</span>
                                    </div>
                                )}
                                {p.email && (
                                    <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-gray-400">
                                        <Mail size={16} className="text-brand-800/50" />
                                        <span className="truncate">{p.email}</span>
                                    </div>
                                )}
                                {p.direccion && (
                                    <div className="flex items-start gap-3 text-sm text-slate-500 dark:text-gray-400">
                                        <MapPin size={16} className="text-brand-800/50 mt-0.5" />
                                        <span className="line-clamp-2">{p.direccion}</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-6 sm:p-8 rounded-3xl w-full max-w-xl shadow-2xl"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="titulo-pagina">{editing ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="boton-icono"><X /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {formError && (
                                <div className="bg-error-500/10 border border-error-500/30 text-error-400 px-4 py-3 rounded-xl text-sm">{formError}</div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label htmlFor="proveedor-razon-social" className="etiqueta-campo">Razón Social</label>
                                    <input id="proveedor-razon-social"
                                        required
                                        className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                        value={formData.nombre}
                                        onChange={e => {
                                            const val = e.target.value;
                                            const cursor = e.target.selectionStart;
                                            // En mayúsculas, como va en un comprobante. Antes se ponía
                                            // mayúscula inicial por palabra, y eso rompe lo que no es un
                                            // nombre común: «ECA S.A» quedaba «Eca S.a». Además el usuario
                                            // escribía en mayúsculas y el sistema se lo cambiaba sin decir
                                            // por qué, que es de las cosas que más desconciertan.
                                            setFormData({...formData, nombre: val.toUpperCase()});
                                            requestAnimationFrame(() => e.target.setSelectionRange(cursor, cursor));
                                        }}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="proveedor-ruc" className="etiqueta-campo">RUC</label>
                                    <input id="proveedor-ruc"
                                        className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                        value={formData.ruc}
                                        onChange={e => setFormData({...formData, ruc: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="proveedor-contacto" className="etiqueta-campo">Contacto</label>
                                    <input id="proveedor-contacto"
                                        className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                        value={formData.contacto}
                                        onChange={e => setFormData({...formData, contacto: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="proveedor-telefono" className="etiqueta-campo">Teléfono</label>
                                    <input id="proveedor-telefono"
                                        className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                        value={formData.telefono}
                                        onChange={e => setFormData({...formData, telefono: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="proveedor-email" className="etiqueta-campo">Email</label>
                                    <input id="proveedor-email"
                                        type="email"
                                        className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                        value={formData.email}
                                        onChange={e => setFormData({...formData, email: e.target.value})}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="proveedor-direccion" className="etiqueta-campo">Dirección</label>
                                    <textarea id="proveedor-direccion"
                                        rows="2"
                                        className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                        value={formData.direccion}
                                        onChange={e => setFormData({...formData, direccion: e.target.value})}
                                    />
                                </div>
                            </div>

                            <button className="w-full bg-brand-500 hover:bg-brand-600 text-negro-950 font-bold py-4 rounded-2xl transition-all shadow-lg shadow-brand-500/20 mt-4 active:scale-[0.98]">
                                {editing ? 'Actualizar Proveedor' : 'Guardar Proveedor'}
                            </button>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
